import type { ReactNode } from 'react';
import { forwardRef, useRef, useState } from 'react';

import type { ReportSection } from './types';
import { ctw } from '@/common';
import { SectionObserver } from '../composites';

type BaseReportProps = {
  sections: ReportSection[];
  disableSectionObserver?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
};

export const BaseReport = forwardRef<HTMLDivElement, BaseReportProps>(
  ({ sections, disableSectionObserver = false, header, footer }, ref) => {
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    const observerSections = sections.map(section => ({
      ...section,
      title: section.title ?? '',
    }));

    return (
      <div
        className={ctw(
          'mx-auto flex flex-col ps-2 transition-all duration-300',
          isSidebarOpen ? 'max-w-8xl' : 'max-w-9xl',
        )}
      >
        {header && <div className="w-full">{header}</div>}
        <div className="flex justify-center">
          <div className="max-w-[62.5rem] overflow-y-visible transition-all duration-300" ref={ref}>
            {sections.map(section => (
              <div
                key={section.id}
                id={section.id}
                ref={el => (sectionRefs.current[section.id] = el)}
                className="min-h-[100px]"
              >
                {section.Component}
              </div>
            ))}
            {footer}
          </div>

          {!disableSectionObserver && (
            <SectionObserver
              sections={observerSections}
              sectionRefs={sectionRefs}
              isSidebarOpen={isSidebarOpen}
              onSidebarToggle={setIsSidebarOpen}
            />
          )}
        </div>
      </div>
    );
  },
);

BaseReport.displayName = 'BaseReport';
